// src/services/sponsorApi.js
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

// In-memory access token for sponsor
let sponsorAccessToken = null;

export const setSponsorAccessToken = (token) => {
    sponsorAccessToken = token;
};

export const getSponsorAccessToken = () => sponsorAccessToken;

export const clearSponsorAccessToken = () => {
    sponsorAccessToken = null;
};

// Create separate axios instance for sponsor API
const sponsorApi = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Send cookies (refresh token) with requests
});

// Track whether a refresh is in progress
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Request interceptor - use in-memory sponsor token
sponsorApi.interceptors.request.use(
    (config) => {
        const token = sponsorAccessToken;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor with silent refresh
sponsorApi.interceptors.response.use(
    (response) => response.data,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't try to refresh if the failed request IS the refresh request
            if (originalRequest.url?.includes('/sponsor/refresh')) {
                clearSponsorAccessToken();
                if (!window.location.pathname.includes('/sponsor/login')) {
                    window.location.href = '/sponsor/login';
                }
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return sponsorApi(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post(
                    `${API_BASE_URL}/sponsor/refresh`,
                    {},
                    { withCredentials: true }
                );

                const newToken = response.data?.data?.token;
                if (newToken) {
                    setSponsorAccessToken(newToken);
                    processQueue(null, newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return sponsorApi(originalRequest);
                }
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearSponsorAccessToken();
                if (!window.location.pathname.includes('/sponsor/login')) {
                    window.location.href = '/sponsor/login';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // For non-401 errors, show toast
        if (error.response?.status !== 401) {
            if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                error.response.data.errors.forEach(err => {
                    toast.error(err);
                });
            } else {
                const message = error.response?.data?.message || 'An error occurred';
                toast.error(message);
            }
        }

        return Promise.reject(error);
    }
);

// Auth endpoints
export const sponsorAuthAPI = {
    login: (credentials) => sponsorApi.post('/sponsor/login', credentials),
    apply: (applicationData) => sponsorApi.post('/sponsor/apply', applicationData),
    getMe: () => sponsorApi.get('/sponsor/me'),
    updatePassword: (data) => sponsorApi.put('/sponsor/password', data),
    updateEmail: (data) => sponsorApi.put('/sponsor/email', data),
    uploadLogo: (formData) => sponsorApi.post('/sponsor/logo', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    }),
    removeLogo: () => sponsorApi.delete('/sponsor/logo'),
    refresh: () => axios.post(`${API_BASE_URL}/sponsor/refresh`, {}, { withCredentials: true }),
    logout: () => sponsorApi.post('/sponsor/logout'),
};

// Events endpoints
export const sponsorEventsAPI = {
    getSponsorableEvents: (params = {}) => sponsorApi.get('/sponsor/events', { params }),
    getEventDetails: (id) => sponsorApi.get(`/sponsor/events/${id}`),
    submitInterest: (eventId, data) => sponsorApi.post(`/sponsor/events/${eventId}/interest`, data),
    editInterest: (eventId, data) => sponsorApi.put(`/sponsor/events/${eventId}/interest`, data),
    getMyInterests: (params = {}) => sponsorApi.get('/sponsor/interests', { params }),
};

export default sponsorApi;
